// frontend/src/api/valuationFinancials.ts
//
// VAL-UPLOAD-U1B-1 — typed client for the Financials step: store a file, read
// it, load what was read. Resolves to apps/api/routers/_valuation_financials.py.
// No figure is computed here; every decision (a held year, a held scale) is a
// server call so it is recorded with who and when.

import { apiClient } from '@/api/client'

const eng = (id: string) => `/v1/valuation/engagements/${id}/financials`

/** One stored file as the server lists it (never a blob URL). */
export interface FinancialsDocument {
  doc_id: string
  filename: string
  file_size: number
  sha256: string
  uploaded_by: string
  uploaded_at: string
}

/** One fiscal year; a figure that was not read is absent, never 0. */
export type FinancialsRow = { year: number } & Partial<Record<string, number | null>>

/** Where one figure came from — rendered verbatim. */
export interface CellProvenance {
  filename?: string
  source_label?: string
  locator?: string
  status?: string
  note?: string
}

/** A fiscal year held out of the table until a person decides. */
export interface HeldPeriod {
  year: number
  filename: string
  period_label?: string
  reason: string
}

/** A file whose printed unit (thousands, millions) is unconfirmed. */
export interface HeldScale {
  doc_id: string
  filename: string
  scale_name?: string
  evidence?: string
  prompt?: string
  reason?: string
}

/** A source line the reader could not place. */
export interface UnmappedLine {
  filename: string
  source_label: string
  value: number | null
  locator?: string
  reason?: string
}

export interface FinancialsReport {
  held_period?: HeldPeriod[]
  held_scale?: HeldScale[]
  unmapped?: UnmappedLine[]
  errors?: { filename: string; error: string }[]
  /** Per-run reader verdicts — NOT FinancialsView.documents (the stored files). */
  documents?: { filename: string; kind: string; parser: string }[]
  unassigned_period?: { filename: string; period_label: string; basis?: string; reason: string }[]
  warnings?: { filename: string; warning: string }[]
  run_at?: string
}

/** One stored reading of an operating agreement (core/valuation/operating_agreement.py). */
export interface OperatingAgreementReading {
  doc_id: string
  filename: string
  status: 'read' | 'partly_read' | 'found_not_read'
  members?: { name: string; membership_interest_pct: string | null; page: number | null }[]
  managers_officers?: { name: string; title: string | null; page: number | null }[]
}

/** One total's tie (core/valuation/tieout.py). */
export interface TieoutTie {
  tie: 'gross_profit' | 'operating_income' | 'net_income'
  status: 'ties' | 'differs' | 'not_printed'
  ours: number | null
  printed: number | null
  difference: number | null
  fields: { field: string; label: string; value?: number | null }[]
  suggestion: {
    field: string; field_label: string; client_line: string
    amount: number; da_amount: number; result: number; lines: string[]
  } | null
  decision: {
    decision: 'keep' | 'enter_figure' | 'use_suggestion'
    reason?: string; by: string; at_central: string
  } | null
}

export interface TieoutYear {
  year: number
  ties: TieoutTie[]
  other_income: { printed: number | null }
  accounting: { ok: boolean; unplaced: { source_label: string; value: number | null }[] }
  lines: { source_label: string; value: number | null; mapped_to: string | null; line_no?: number; filename?: string }[]
}

export interface TieoutDecisionBody {
  year: number
  tie: string
  decision: 'keep' | 'enter_figure' | 'use_suggestion'
  field?: string
  value?: number
  reason?: string
}

export interface FinancialsView {
  valuation_engagement_id: string
  rows: FinancialsRow[]
  provenance: Record<string, CellProvenance | unknown>
  last_run: FinancialsReport | null
  operating_agreements?: Record<string, OperatingAgreementReading>
  tieout?: TieoutYear[]
  documents: FinancialsDocument[]
  updated_by: string | null
  updated_at: string | null
}

/** Body of the read call. Years are numbers; scale answers are doc ids. */
export interface ExtractBody {
  doc_ids?: string[]
  confirm_years?: number[]
  discard_years?: number[]
  confirm_scale?: string[]
  decline_scale?: string[]
}

export interface ExtractResponse {
  valuation_engagement_id: string
  documents_read: number
  /** True only when AI actually read a file; the server decides. */
  ai_used: boolean
  report?: FinancialsReport
  message?: string
}

export async function fetchFinancials(id: string): Promise<FinancialsView> {
  const { data } = await apiClient.get<FinancialsView>(eng(id))
  return data
}

export async function uploadFinancialsDocument(id: string, file: File): Promise<{ doc_id: string; filename: string }> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post(`${eng(id)}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function extractFinancials(id: string, body: ExtractBody = {}): Promise<ExtractResponse> {
  const { data } = await apiClient.post<ExtractResponse>(`${eng(id)}/extract`, body)
  return data
}

export async function decideTieout(id: string, body: TieoutDecisionBody): Promise<void> {
  await apiClient.post(`${eng(id)}/tieout/decisions`, body)
}
